import { useState } from 'react';
import { tGroupedRegionWithDistricts } from '../../types';
import { Btn, BtnIconOnly, IconClose } from '../ui';

interface DeleteRegionModalProps {
    region: tGroupedRegionWithDistricts;
    getLocationEquipmentCount: (locationId: string) => number;
    onClose: () => void;
    onConfirm: () => void;
}

export const DeleteRegionModal = ({ region, getLocationEquipmentCount, onClose, onConfirm }: DeleteRegionModalProps) => {
    const [confirmed, setConfirmed] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const totalDistricts = region.districts.length;
    const totalLocations = region.locations.length;
    const totalEquipment = region.locations.reduce((sum, loc) => sum + getLocationEquipmentCount(loc.id), 0);

    const handleDelete = async () => {
        setDeleting(true);
        await onConfirm();
        setDeleting(false);
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
                <div className="modal-header modal-header-danger">
                    <h2>Delete Region?</h2>
                    <BtnIconOnly size="sm" fill="opaque" icon={<IconClose />} ariaLabel="Close" onClick={onClose} />
                </div>
                <div className="modal-body">
                    <div className="modal-warning">
                        <p>
                            <strong>This action cannot be undone.</strong> Deleting <strong>{region.name}</strong> will also permanently delete:
                        </p>
                        <ul>
                            <li>{totalDistricts} district{totalDistricts !== 1 ? 's' : ''}</li>
                            <li>{totalLocations} location{totalLocations !== 1 ? 's' : ''}</li>
                            <li>All location assignments for {totalEquipment} pieces of equipment</li>
                        </ul>
                    </div>
                    <p className="modal-warning-note">
                        Equipment assigned to locations in this region will become unassigned. You'll need to reassign them in the Equipment module.
                    </p>
                    <label className="modal-checkbox">
                        <input type="checkbox" checked={confirmed} onChange={e => setConfirmed(e.target.checked)} />
                        I understand this will permanently delete the region and all nested districts and locations.
                    </label>
                </div>
                <div className="modal-footer">
                    <Btn variant="secondary-outlined" buttonText="Cancel" buttonOnClick={onClose} />
                    <Btn
                        variant="danger"
                        buttonText={deleting ? 'Deleting...' : 'Delete Region'}
                        buttonDisabled={!confirmed || deleting}
                        buttonOnClick={handleDelete}
                    />
                </div>
            </div>
        </div>
    );
};
